import { defineStore } from 'pinia';
import { peekRequest, initiateVideo, endSession } from '@/api/session';
import { useUserStore } from './user';
import { usePerformerStore } from './performer';
import notifications, { type VideoChatUpdate } from '@/socket';
import { usePaymentStore } from './payment';
import type { Stream } from '@/ontology/stream';

export type PeekStatus = 'idle' | 'checking' | 'initiating' | 'initializing' | 'active' | 'ending' | 'ended' | 'error';

interface State {
    status: PeekStatus;
    endReason: string;
    stream?: Stream;
    //performer ID
    performer: number;
}

export const usePeekStore = defineStore('peek', {
    state: (): State => ({
        status: 'idle',
        endReason: '',
        stream: undefined,
        performer: -1
    }),
    actions: {
        initialize() {
            notifications.subscribe('videoChat', (update: VideoChatUpdate) => {
                if (!this.stream) {
                    return;
                }
                if (this.stream.id != update.id) {
                    return;
                }

                //Hoofdsessie is geëindigd, plus meekijken uitgezet
                //'meekijken niet meer beschikbaar'
                /*clientId:56121261
                  id:"fb1c53d1821e72f91773fb37e4446929"
                    message:"MAIN_ENDED"
                    performerId:158
                    type:"RESPONSE"
                    value:false*/
                if (update.message == 'MAIN_ENDED') {
                    //console.log('main ended yo');
                    this.setStatus('ended');
                    //meekijken niet meer beschikbaar..
                    this.endReason = 'MAIN_ENDED';
                }

                //Timeout: startTimerDecive is nooit langs gekomen.
                /*
                    clientId:56121261
                    id:"0e1e9fac7e34c15f254c8de1e7bcdd75"
                    message:"TIMEOUT"
                    performerId:158
                    type:"RESPONSE"
                    value:false
                */
                if (update.message == 'TIMEOUT') {
                    //console.log('timetout yo');
                    this.setStatus('ended');
                    this.endReason = 'TIMEOUT';
                }
                //Geen credits meer
                /*{
                    "performerId": 158,
                    "clientId": 56121261,
                    "type": "RESPONSE",
                    "value": false,
                    "id": "302a4d74de1d7b2fc909decfacb48d4e",
                    "message": "BROKE"
                }*/
                if (update.message == 'BROKE') {
                    //console.log('blut yo');
                    this.setStatus('ended');
                    this.endReason = 'BROKE';
                }

                if (update.message == 'HANGUP') {
                    //console.log('blut yo');
                    this.setStatus('ended');
                    this.endReason = 'HANGUP';
                }
            });
        },

        async start(performerId: number, ivrCode?: string, name?: string) {
            //stop any active peekers
            if (!['ended', 'error', 'idle'].includes(this.status)) {
                await this.end();
            }

            const account = useUserStore().account;
            if (name) {
                account.username = name;
            }

            this.setStatus('checking');
            this.performer = performerId;

            const payment = usePaymentStore();

            if (!ivrCode) {
                //check if there's an active payment stream with ivrCode. If so, use that
                if (payment.code && payment.start) {
                    ivrCode = payment.code;
                }
            }

            payment.authorize('peek', ivrCode ? 'ivr' : 'credits', ivrCode);

            const { error: requestError } = await peekRequest({
                performerId,
                clientId: account.id!,
                name: account.username || 'anonymous',
                ivrCode
            });

            if (requestError) {
                this.setStatus('error');
                payment.authorizationFailed(requestError.message);
                return requestError;
            }

            this.setStatus('initiating');

            const performers = usePerformerStore();
            let performer = performers.getById(performerId);
            if (!performer) {
                //we'll need to load the performer by id...
                performer = await performers.loadPerformerById(performerId);
            }

            const { error: initiateError, result } = await initiateVideo({
                advert: performer.advertNumber,
                clientId: account.id!,
                ivrCode,
                name: account.username || 'anonymous'
            });

            if (initiateError) {
                this.setStatus('error');
                payment.authorizationFailed(initiateError.message);
                return initiateError;
            }

            if (!result) {
                throw new Error('Impossible');
            }

            this.setStatus('initializing');
            this.$patch({
                stream: {
                    name: result.playStream,
                    token: result.playToken,
                    server: result.playWowza,
                    status: 'idle',
                    attempt: 1
                }
            });
        },

        async end() {
            //console.log(`end peeking while ${this.status}`);
            //why end a session that is already end(ed)(ing)?
            if (['ending', 'ended'].includes(this.status)) {
                return;
            }
            this.setStatus('ending');
            const { error } = await endSession();
            if (error) {
                //TODO: handle this nearly impossible error; probably an error cause the end request was after
                // the session was ended already?
            }
            this.endReason = 'CLICK';
            this.setStatus('ended');
        },

        async next() {
            const peekers = usePerformerStore().getSlice('peekers');
            const ix = peekers.items.indexOf(this.performer);
            let theNext = ix + 1;
            if (theNext >= peekers.items.length) theNext = 0;

            return await this.start(peekers.items[theNext]);
        },

        setStatus(value: PeekStatus) {
            if (this.status == value) {
                return;
            }

            //console.log(`peek status van ${this.status} naar ${value}`);

            this.status = value;
            notifications.sendLocalEvent('status_change', {
                sender: 'peek',
                value
            });

            if (this.status == 'ended') {
                this.stream = undefined;
            }
        },

        streamStatusChange(newValue: string) {
            if (!this.stream) {
                //the stream is reset before a final status message can come in for the stream
                return;
            }

            //console.log(`stream status van ${this.stream.status} naar ${newValue}`)
            this.stream.status = newValue;
            //I don't care what the new status of a stream is, if this session is terminating already
            if (['ending', 'ended'].includes(this.status)) {
                return;
            }

            //I don't care about intermittent status between initializing and active,
            //to the peek session it's all intializing

            if (newValue == 'active') {
                this.setStatus('active');
                //"really" activate that session
                notifications.sendEvent({
                    receiverType: 'ROLE_PERFORMER',
                    receiverId: this.performer,
                    event: 'videoChat',
                    content: {
                        type: 'START_TIMER_DEVICE',
                        clientId: useUserStore().account.id!,
                        performerId: this.performer,
                        value: undefined
                    }
                });
            }

            if (['destroying', 'destroying_janus'].includes(newValue)) {
                //console.log('janus being destroyed..');
                this.setStatus('ended');
                this.endReason = 'DISCONNECTED';
            }
        }
    }
});
